import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { Helmet } from 'react-helmet';

// Define margin and padding constants
const MARGIN_BOTTOM = '18px';
const PADDING_LEFT = '36px';

const PrivacyPolicy = () => {
  return (
    <Container sx={{ paddingTop: 4 }}>
      <Helmet>
        <title>Privacy Policy | SalaryTools.co.uk</title>
        <meta
          name="description"
          content="Read the Privacy Policy for SalaryTools.co.uk to learn how we collect, use, and protect your personal data in compliance with UK data protection laws and GDPR."
        />
        <link rel="canonical" href="https://salarytools.co.uk/privacy-policy" />
      </Helmet>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        Privacy Policy
      </Typography>
      <Box>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          Your privacy is important to us. We are committed to protecting and respecting your privacy in accordance with the UK Data Protection Act 2018, the General Data Protection Regulation (GDPR), and other applicable laws. This policy explains how we collect, use, and safeguard your personal data when you visit our website.
        </Typography>

        <Typography variant="h6" sx={{ marginTop: 4 }}>
          1. Information We Collect
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          We collect personal information when you visit our website, interact with our content, and use our services. This may include, but is not limited to, your name, email address, and any other data you provide to us. We may also collect technical data such as your IP address, browser type, and device information through cookies and analytics tools.
        </Typography>

        <Typography variant="h6">
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          We use the information we collect for the following purposes:
        </Typography>
        
        <ul style={{ paddingLeft: PADDING_LEFT, marginTop: 0 }}>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>To provide, maintain, and improve our services</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>To personalise your experience on our website</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>To send you updates, newsletters, and marketing communications (with your consent where required)</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>To respond to your inquiries and provide customer support</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>To comply with legal obligations and protect our rights</li>
        </ul>
        
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          We will not use your personal data for any purpose other than the purposes described in this policy.
        </Typography>

        <Typography variant="h6">
          3. Legal Basis for Processing Your Data
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          Under the GDPR, we are required to have a legal basis for processing your personal data. The legal bases we rely on include:
        </Typography>

        <ul style={{ paddingLeft: PADDING_LEFT, marginTop: 0 }}>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>Consent – where you have given us explicit consent to process your personal data</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>Contractual necessity – where processing is necessary for the performance of a contract with you</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>Legitimate interests – where we have a legitimate interest in processing your data, provided it does not override your rights and freedoms</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>Legal obligation – where processing is necessary to comply with our legal obligations</li>
        </ul>

        <Typography variant="h6">
          4. Data Retention
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          We will retain your personal data for as long as necessary to fulfil the purposes outlined in this Privacy Policy, or as required by law. When your personal data is no longer needed, we will securely delete or anonymise it.
        </Typography>

        <Typography variant="h6">
          5. Data Protection
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          We implement appropriate technical and organisational measures to protect your personal data from unauthorised access, use, disclosure, or destruction. However, please note that no method of transmission over the internet is entirely secure, and we cannot guarantee the absolute security of your information.
        </Typography>

        <Typography variant="h6">
          6. Your Rights
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          Under the GDPR and UK data protection laws, you have certain rights regarding your personal data. These include:
        </Typography>

        <ul style={{ paddingLeft: PADDING_LEFT, marginTop: 0 }}>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>The right to access your data</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>The right to rectification of inaccurate data</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>The right to erasure (also known as the "right to be forgotten")</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>The right to restrict processing of your data</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>The right to data portability</li>
          <li style={{ marginBottom: MARGIN_BOTTOM }}>The right to object to processing of your data</li>
        </ul>

        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          If you wish to exercise any of these rights, please contact us using the details provided below.
        </Typography>

        <Typography variant="h6">
          7. Third-Party Services
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          We may share your personal data with trusted third-party service providers who assist us in operating our website and providing services to you. These third parties are contractually obligated to protect your data and process it only for the purposes specified by us.
        </Typography>

        <Typography variant="h6">
          8. Cookies and Tracking Technologies
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          We use cookies and similar tracking technologies to collect and store information when you visit our website. These technologies help us understand how you interact with our website and improve your experience. You can control the use of cookies through your browser settings, but please note that disabling cookies may affect your experience on our website.
        </Typography>

        <Typography variant="h6">
          9. Changes to This Privacy Policy
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          We reserve the right to update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically to stay informed about how we are protecting your personal data.
        </Typography>

        <Typography variant="body2" sx={{ marginTop: 4 }}>
          If you have any questions or concerns about our Privacy Policy or the way we handle your personal data, please contact be emailing: contact [at] salarytools.co.uk (replacing the [at] with an @ symbol).
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
